import { useState } from "react";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import Loader from "../../components/ui/Loader";
import {
	useDeletePropertyMutation,
	useListPropertiesQuery,
} from "../../store/api/propertiesApi";
import { arrayToCsv, downloadCsv, formatDateForCsv } from "../../utils/csvExport";
import AddPropertyModal from "./components/AddPropertyModal";
import PropertyDetailPanel from "./components/PropertyDetailPanel";
import PropertyFilters from "./components/PropertyFilters";
import PropertySidebar from "./components/PropertySidebar";
import PropertyTable from "./components/PropertyTable";

const PropertyManagement = () => {
	const [page, setPage] = useState(1);
	const [filters, setFilters] = useState({
		search: "",
		status: "",
		property_type: "",
		city: "",
		state: "",
	});
	const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
	const [selectedProperty, setSelectedProperty] = useState<any>(null);
	const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false);
	const [isAddModalOpen, setIsAddModalOpen] = useState(false);

	const {
		data: propertiesData,
		isLoading,
		error,
		refetch,
	} = useListPropertiesQuery({
		page,
		page_size: 20,
		...filters,
		search: filters.search || undefined,
		status: filters.status || undefined,
		property_type: filters.property_type || undefined,
		city: filters.city || undefined,
		state: filters.state || undefined,
		ordering: "-created_at",
	});

	const [deleteProperty, { isLoading: isDeleting }] =
		useDeletePropertyMutation();

	const handleFiltersChange = (newFilters: any) => {
		setFilters((prev) => ({
			...prev,
			...newFilters,
		}));
		setPage(1); // Reset to first page when filters change
	};

	const handleClearFilters = () => {
		setFilters({
			search: "",
			status: "",
			property_type: "",
			city: "",
			state: "",
		});
		setPage(1);
	};

	const handlePropertySelect = (propertyId: string) => {
		setSelectedProperties((prev) =>
			prev.includes(propertyId)
				? prev.filter((id) => id !== propertyId)
				: [...prev, propertyId],
		);
	};

	const handleSelectAll = () => {
		if (!propertiesData) return;
		setSelectedProperties(
			selectedProperties.length === propertiesData.results.length
				? []
				: propertiesData.results.map((p) => p.id),
		);
	};

	const handlePropertyClick = (property: any) => {
		setSelectedProperty(property);
		setIsDetailPanelOpen(true);
	};

	const handleEditProperty = (property: any) => {
		setSelectedProperty(property);
		setIsAddModalOpen(true);
	};

	const handleDeleteProperty = async (propertyId: string) => {
		if (!window.confirm("Are you sure you want to delete this property?")) {
			return;
		}

		try {
			await deleteProperty(propertyId).unwrap();
			refetch();
			setSelectedProperties((prev) => prev.filter((id) => id !== propertyId));
		} catch (error) {
			console.error("Failed to delete property:", error);
			alert("Failed to delete property. Please try again.");
		}
	};

	const handleBulkAction = async (action: string, selectedIds: string[]) => {
		switch (action) {
			case "export":
				if (!propertiesData) return;
				// Export all properties or only selected ones
				const propertiesToExport =
					selectedIds.length > 0
						? propertiesData.results.filter((p) => selectedIds.includes(p.id))
						: propertiesData.results;

				const csvData = propertiesToExport.map((property) => ({
					Name: property.name || "",
					Address: property.address || "",
					City: property.city || "",
					State: property.state || "",
					"Zip Code": property.zip_code || "",
					Country: property.country || "",
					Type: property.property_type || "",
					Status: property.status || "",
					Bedrooms: property.bedrooms?.toString() || "",
					Bathrooms: property.bathrooms?.toString() || "",
					"Square Feet": property.square_feet?.toString() || "",
					"Monthly Rent": property.monthly_rent?.toString() || "",
					"Purchase Price": property.purchase_price?.toString() || "",
					"Security Deposit": property.security_deposit?.toString() || "",
					Features: property.features?.join("; ") || "",
					"Created At": formatDateForCsv(property.created_at),
					"Updated At": formatDateForCsv(property.updated_at),
				}));

				const csvContent = arrayToCsv(csvData, [
					"Name",
					"Address",
					"City",
					"State",
					"Zip Code",
					"Country",
					"Type",
					"Status",
					"Bedrooms",
					"Bathrooms",
					"Square Feet",
					"Monthly Rent",
					"Purchase Price",
					"Security Deposit",
					"Features",
					"Created At",
					"Updated At",
				]);
				downloadCsv(csvContent, "properties-export");
				break;
			case "delete":
				if (
					!window.confirm(
						`Are you sure you want to delete ${selectedIds.length} properties?`,
					)
				) {
					return;
				}
				try {
					await Promise.all(
						selectedIds.map((id) => deleteProperty(id).unwrap()),
					);
					refetch();
					setSelectedProperties([]);
				} catch (error) {
					console.error("Failed to delete properties:", error);
					alert("Failed to delete some properties. Please try again.");
				}
				break;
			default:
				break;
		}
	};

	const handleAddPropertySuccess = () => {
		setIsAddModalOpen(false);
		refetch();
	};

	if (error) {
		return (
			<div className="w-full flex flex-col items-center justify-center min-h-screen space-y-4">
				<Icon name="AlertCircle" size={48} className="text-destructive" />
				<h2 className="text-xl font-semibold text-foreground">
					Error Loading Properties
				</h2>
				<p className="text-muted-foreground">
					Failed to load properties. Please try again.
				</p>
				<Button onClick={() => refetch()}>Retry</Button>
			</div>
		);
	}

	const properties = propertiesData?.results || [];

	return (
		<div className="w-full">
			<div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
				{/* Page Header */}
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
					<div>
						<h1 className="text-2xl sm:text-3xl font-bold text-foreground">
							Property Management
						</h1>
						<p className="text-muted-foreground mt-1">
							Manage your property portfolio with comprehensive tools and
							insights
						</p>
					</div>

					<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 sm:flex-shrink-0">
						<Button
							variant="outline"
							iconName="Download"
							iconPosition="left"
							className="w-full sm:w-auto"
						>
							Export
						</Button>
						<Button variant="outline" iconName="Upload" iconPosition="left">
							Import
						</Button>
						<Button
							onClick={() => setIsAddModalOpen(true)}
							iconName="Plus"
							iconPosition="left"
						>
							Add Property
						</Button>
					</div>
				</div>

				{/* Main Content */}
				<div className="grid grid-cols-1 xl:grid-cols-4 gap-4 sm:gap-6">
					{/* Main Content Area */}
					<div className="xl:col-span-3 space-y-4 sm:space-y-6">
						{/* Filters */}
						<PropertyFilters
							onFiltersChange={handleFiltersChange}
							onClearFilters={handleClearFilters}
						/>

						{/* Properties Table */}
						{isLoading ? (
							<Loader
								size="lg"
								showText
								text="Loading properties..."
								className="h-64"
							/>
						) : (
							<>
								<PropertyTable
									properties={properties}
									selectedProperties={selectedProperties}
									onPropertySelect={handlePropertySelect}
									onSelectAll={handleSelectAll}
									onPropertyClick={handlePropertyClick}
									onEditProperty={handleEditProperty}
									onDeleteProperty={handleDeleteProperty}
								/>

								{/* Empty State */}
								{properties.length === 0 && (
									<div className="bg-card rounded-lg border border-border p-6 sm:p-12 text-center">
										<Icon
											name="Building2"
											size={64}
											className="text-muted-foreground mx-auto mb-4"
										/>
										<h3 className="text-lg font-medium text-foreground mb-2">
											No properties found
										</h3>
										<p className="text-muted-foreground mb-6">
											No properties match your current filters. Try adjusting
											your search criteria.
										</p>
										<Button
											onClick={() => setIsAddModalOpen(true)}
											iconName="Plus"
											iconPosition="left"
										>
											Add Your First Property
										</Button>
									</div>
								)}
							</>
						)}

						{/* Pagination */}
						{propertiesData &&
							(propertiesData.next || propertiesData.previous) && (
								<div className="mt-6 flex items-center justify-between">
									<Button
										variant="outline"
										onClick={() => setPage((p) => Math.max(1, p - 1))}
										disabled={!propertiesData.previous}
										iconName="ChevronLeft"
										iconPosition="left"
									>
										Previous
									</Button>
									<span className="text-sm text-muted-foreground">
										Page {page} of {Math.ceil(propertiesData.count / 20)}
									</span>
									<Button
										variant="outline"
										onClick={() => setPage((p) => p + 1)}
										disabled={!propertiesData.next}
										iconName="ChevronRight"
										iconPosition="right"
									>
										Next
									</Button>
								</div>
							)}
					</div>

					{/* Sidebar */}
					<div className="xl:col-span-1">
						<PropertySidebar
							properties={properties}
							selectedProperties={selectedProperties}
							onBulkAction={handleBulkAction}
						/>
					</div>
				</div>
				{/* Property Detail Panel */}
				<PropertyDetailPanel
					property={selectedProperty}
					isOpen={isDetailPanelOpen}
					onClose={() => setIsDetailPanelOpen(false)}
					onEdit={handleEditProperty}
				/>

				{/* Add Property Modal */}
				<AddPropertyModal
					isOpen={isAddModalOpen}
					onClose={() => {
						setIsAddModalOpen(false);
						setSelectedProperty(null);
					}}
					onSuccess={handleAddPropertySuccess}
					property={selectedProperty}
				/>
			</div>
		</div>
	);
};

export default PropertyManagement;
